import { Subject } from 'rxjs'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import type { Question } from '@/features/jobs/questions/questions'
import type { ActivationLifecycle } from '@/infra/activation'
import { QuestionValidationResult } from '@/features/jobs/questions/question-validation-result'

/**
 * Base view-model for question answer editors.
 */
export abstract class QuestionAnswerEditorViewModelBase<T extends Question>
  implements ActivationLifecycle
{
  /**
   * Private subject used to notify of answer changes.
   */
  private readonly _answerChangedSubject = new Subject<void>()

  /**
   * Private subject used to notify that the user can continue to the
   * next question if applicable.
   *
   * @private
   */
  private readonly _continueSubject = new Subject<void>()

  /**
   * Lifecycle manager.
   *
   * @protected
   */
  protected readonly lifecycle = new LifecycleTracker()

  /**
   * Emits when the answer is changed, but only due to some form of user input.
   */
  readonly answerChanged$ = this._answerChangedSubject.asObservable()

  /**
   * Used to notify that the user can continue to the next question
   * if applicable.
   */
  readonly continue$ = this._continueSubject.asObservable()

  /**
   * The question's type.
   */
  abstract readonly type: T['type']

  /**
   * Whether the question has been answered.
   */
  abstract get isAnswered(): boolean

  /**
   * The answer as a text string.
   */
  abstract get textAnswer(): string | null

  /**
   * The current state of the VM as an answer object.
   */
  abstract get answer(): T['answer']

  /**
   * Notifies that the answer has changed.
   *
   * @protected
   */
  protected notifyAnswerChanged() {
    this._answerChangedSubject.next()
  }

  /**
   * Request that the user continues to the next question.
   * Useful for questions like single-select that wishes to
   * trigger the "continue" flow on the user's behalf.
   *
   * @protected
   */
  protected notifyContinue() {
    this._continueSubject.next()
  }

  /** @inheritDoc */
  activate(): unknown {
    // No-op — can be overridden
    return
  }

  /** @inheritDoc */
  deactivate(): unknown {
    // No-op — can be overridden
    return
  }

  /**
   * Lets the editor know that any pending changes should be flushed.
   * If there are any changes, the implementation should update its answer and
   * call {@link notifyAnswerChanged}.
   */
  flush(): unknown {
    // No-op — can be overridden
    return
  }

  /**
   * Sets the editor's properties from the question.
   *
   * @param question
   */
  abstract set(question: T): void

  /**
   * Validates the question answer.
   *
   * @virtual
   */
  validate(): QuestionValidationResult {
    return QuestionValidationResult.ok()
  }
}
